import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild, ElementRef, HostListener } from '@angular/core';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AudienceService, UserAudienceResponse, UnifiedAudience, VeevaTaggedAudience } from '../../services/audience.service';
import { RdsFormFieldModule, RdsMultiAutocompleteComponent } from '@rds/angular-components';

@Component({
  selector: 'app-audience-selector',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RdsFormFieldModule,
    RdsMultiAutocompleteComponent
],
  template: `
    <div class="audience-selector">
      @if (isViewer) {
        <!-- Viewer: Simple list only -->
        <div class="list-content">
          @if (selectedAudiences.length === 0) {
            <rds-form-field labelPosition="side-left">
              <input rdsInput [value]="'None'" readonly disabled />
            </rds-form-field>
          }
          @for (audience of selectedAudiences; track audience.id) {
            <rds-form-field labelPosition="side-left">
              <input rdsInput [value]="audience.name" readonly disabled />
            </rds-form-field>
          }
        </div>
      } @else {
        <!-- Editor: Dropdown + list below -->
        <div class="editor-content">
          <rds-form-field id="audience-dropdown">
            <rds-multi-autocomplete 
              [formControl]="audienceSelectControl"
              [options]="formattedAudiences"
              placeholder="Select…"
              [highlightQuery]="false"
              minSearchChars="0"
              debounce="0"
              displayProperty="name"
              uniqProperty="id">
            </rds-multi-autocomplete>
          </rds-form-field>
          @if (selectedAudiences.length > 0) {
            <div class="selected-list">
              @for (audience of selectedAudiences; track audience.id) {
                <rds-form-field labelPosition="side-left">
                  <input rdsInput [value]="audience.name" readonly />
                </rds-form-field>
              }
            </div>
          }
        </div>
      }
    </div>
  ` as any,
  styles: [`
    .audience-selector {
      .list-content,
      .selected-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      
      .editor-content {
        display: flex;
        flex-direction: column;
        gap: 16px;
      }
      
      ::ng-deep #audience-dropdown {
        rds-multi-autocomplete {
          .rds-chips__chip {
            visibility: hidden !important;
            position: absolute !important;
            width: 0 !important;
            height: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
          }
        }
      }
      
      /* Hide any duplicate labels when used in pillars-audiences-section */
      :host-context(app-pillars-audiences-section) {
        .section-label,
        .field-label,
        h3, h4, h5,
        .ui-heading,
        [data-label] {
          display: none !important;
        }
      }
    }
  `]
})
export class AudienceSelectorComponent implements OnInit, OnDestroy {
  @Input() entityType!: string;
  @Input() entityId!: string;
  @Input() isViewer: boolean = false;
  @Input() initialAudiences: VeevaTaggedAudience[] = []; // Receive initial audiences from parent
  @Output() audienceChanged = new EventEmitter<{ audienceIds: number[], audiences: UnifiedAudience[] }>();
  @ViewChild(RdsMultiAutocompleteComponent) multiAutocomplete!: RdsMultiAutocompleteComponent<UnifiedAudience>;

  audienceSelectControl = new FormControl<UnifiedAudience[] | null>(null);
  allAudiences: UnifiedAudience[] = [];
  formattedAudiences: UnifiedAudience[] = [];
  selectedAudiences: UnifiedAudience[] = [];
  originalAudienceIds: number[] = [];
  hasPendingChanges: boolean = false;

  private subscriptions: Subscription[] = [];

  constructor(private audienceService: AudienceService, private elementRef: ElementRef) {}

  ngOnInit() {
    this.loadData();
    this.setupAudienceSelection();
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private loadData() {
    // Load all unified audiences
    const allAudiencesSub = this.audienceService.getUnifiedAudiences().subscribe({
      next: (audiences) => {
        this.allAudiences = audiences;
        this.formattedAudiences = audiences;
        
        // Convert initial VeevaTaggedAudience to UnifiedAudience format using fetched unified audiences
        if (this.initialAudiences && this.initialAudiences.length > 0) {
          const unifiedAudiences: UnifiedAudience[] = this.initialAudiences
            .map(a => {
              // Find the matching unified audience by veeva_audience_id or veeva_specialist_id
              return this.allAudiences.find(ua => 
                (a.veeva_audience_id !== null && ua.veeva_audience_id === a.veeva_audience_id) ||
                (a.veeva_specialist_id !== null && ua.veeva_specialist_id === a.veeva_specialist_id)
              );
            })
            .filter(a => a !== undefined) as UnifiedAudience[];
          
          this.selectedAudiences = unifiedAudiences;
          this.originalAudienceIds = unifiedAudiences.map(a => a.id);
          this.audienceSelectControl.setValue(unifiedAudiences);
        }
      },
      error: (err) => console.error('Error loading unified audiences:', err)
    });
    this.subscriptions.push(allAudiencesSub);
  }

  private setupAudienceSelection() {
    const selectionSub = this.audienceSelectControl.valueChanges.subscribe(selectedAudiences => {
      // Prevent viewers from making changes
      if (this.isViewer) {
        return;
      }
      
      const currentAudiences = selectedAudiences || [];
      const currentIds = currentAudiences.map(a => a.id);
      
      // Always update the display
      this.selectedAudiences = currentAudiences;
      
      // Check if selection has changed
      const hasChanged = JSON.stringify(currentIds.sort()) !== JSON.stringify(this.originalAudienceIds.sort());
      
      if (hasChanged) {
        this.hasPendingChanges = true;

        // Emit the change event
        this.audienceChanged.emit({ 
          audienceIds: currentIds, 
          audiences: currentAudiences 
        });
      }
    });
    this.subscriptions.push(selectionSub);
  }

  // Method to be called by parent component when saving
  getCurrentSelection(): { audienceIds: number[], audiences: UnifiedAudience[] } {
    return {
      audienceIds: this.selectedAudiences.map(a => a.id),
      audiences: this.selectedAudiences
    };
  }

  // Method to be called by parent component after successful save
  markAsSaved() {
    this.originalAudienceIds = this.selectedAudiences.map(a => a.id);
    this.hasPendingChanges = false;
  }

  // Method to reset to original state
  reset() {
    const formattedSelected = this.originalAudienceIds
      .map(id => this.formattedAudiences.find(a => a.id === id))
      .filter(a => a !== undefined) as UnifiedAudience[];
    this.selectedAudiences = formattedSelected;
    this.audienceSelectControl.setValue(formattedSelected);
    this.hasPendingChanges = false;
  }

  // Method to remove a specific audience
  removeAudience(audienceToRemove: UnifiedAudience) {
    // Prevent viewers from removing audiences
    if (this.isViewer) {
      return;
    }
    
    this.selectedAudiences = this.selectedAudiences.filter(a => a.id !== audienceToRemove.id);
    this.audienceSelectControl.setValue(this.selectedAudiences);
    this.hasPendingChanges = JSON.stringify(this.selectedAudiences.map(a => a.id).sort()) !== JSON.stringify(this.originalAudienceIds.sort());
    
    // Emit the change event
    this.audienceChanged.emit({ 
      audienceIds: this.selectedAudiences.map(a => a.id), 
      audiences: this.selectedAudiences 
    });
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Check if click is inside this component's autocomplete or overlay
    const clickedInsideComponent = this.elementRef.nativeElement.contains(target);
    const clickedInsideOverlay = target.closest('.rds-autocomplete-panel') || 
                                  target.closest('.cdk-overlay-container');
    if (!clickedInsideComponent && !clickedInsideOverlay) {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    this.multiAutocomplete?.hide();
  }
}
